/**
 * Display half of the CG-5136 / CG-5136X cell map.
 *
 * Keys and row counts match
 * `services/coms/src/routes/Forms/xlsx/cg5136Layout.ts`. The server owns the
 * Excel columns; this file only drives the card.
 */

export type CgCostFormId = 'CG_5136' | 'CG_5136X';

export type CostDisplayColumn = { key: string; label: string };

export type CostDisplaySection = {
  id: string;
  title: string;
  rowCount: number;
  columns: readonly CostDisplayColumn[];
  rowLabels?: readonly string[];
  placeholders?: Partial<Record<string, readonly string[]>>;
};

const PAY_GRADES_FY25 = [
  'E-1',
  'E-2',
  'E-3',
  'E-4',
  'E-5',
  'E-6',
  'E-7',
  'E-8',
  'E-9',
  'E-10',
  'O-1',
  'O-2',
  'O-3',
  'O-4',
  'O-5',
  'O-6',
  'O-7',
  'O-8',
  'O-9',
  'O-10',
  'W-2',
  'W-3',
  'W-4',
  'GS-3',
  'GS-4',
  'GS-5',
  'GS-6',
  'GS-7',
  'GS-8',
  'GS-9',
] as const;

const PERSONNEL: readonly CostDisplayColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'emplid', label: 'CG EMPLID' },
  { key: 'arc', label: 'A, R, C' },
  { key: 'unit', label: 'Unit' },
  { key: 'duty', label: 'Duty' },
  { key: 'hours', label: 'Daily hours' },
];

const VEHICLE: readonly CostDisplayColumn[] = [
  { key: 'kind', label: 'Vehicle' },
  { key: 'plate', label: 'GSA plate #' },
  { key: 'count', label: '# vehicles' },
  { key: 'miles', label: '# miles' },
];

const AIRCRAFT: readonly CostDisplayColumn[] = [
  { key: 'kind', label: 'Aircraft' },
  { key: 'number', label: 'A/C no.' },
  { key: 'rateBasis', label: 'Rate basis' },
  { key: 'hours', label: 'Daily hours' },
  { key: 'almis', label: 'ALMIS record' },
];

const BOAT: readonly CostDisplayColumn[] = [
  { key: 'kind', label: 'Boat or cutter' },
  { key: 'number', label: 'Number' },
  { key: 'hours', label: 'Daily hours' },
  { key: 'almis', label: 'ALMIS record' },
];

const EQUIPMENT: readonly CostDisplayColumn[] = [
  { key: 'kind', label: 'Equipment' },
  { key: 'hours', label: 'Daily hours' },
];

const MSL: readonly CostDisplayColumn[] = [
  { key: 'name', label: 'Analysis' },
  { key: 'report', label: 'Report number' },
  { key: 'issued', label: 'Issue date' },
  { key: 'cost', label: 'Lab cost' },
];

function amount(nameLabel: string): readonly CostDisplayColumn[] {
  return [
    { key: 'name', label: nameLabel },
    { key: 'award', label: 'Award number' },
    { key: 'amount', label: 'Amount' },
  ];
}

const TRAVEL: readonly CostDisplayColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'award', label: 'Award number' },
  { key: 'issuedBy', label: 'Issued by' },
  { key: 'amount', label: 'Amount' },
];

export const PREVIOUS_COST_FIELDS = [
  { key: 'personnel', label: 'Personnel' },
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'aircraft', label: 'Aircraft' },
  { key: 'boats', label: 'Boats' },
  { key: 'cutters', label: 'Cutters' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'msl', label: 'Marine safety lab' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'travel', label: 'Travel orders' },
  { key: 'reserve', label: 'Reserve salary' },
  { key: 'aux', label: 'Auxiliary patrols' },
  { key: 'prfa', label: 'PRFA' },
  { key: 'mipr', label: 'MIPR' },
  { key: 'contractor', label: 'Contractor' },
] as const;

export const COST_DAY_COUNT = 31;

function personnel(grades: readonly string[]): CostDisplaySection {
  return {
    id: 'personnel',
    title: 'Personnel',
    rowCount: grades.length,
    columns: PERSONNEL,
    rowLabels: grades,
    placeholders: {
      name: grades.map(() => 'Last, First, M.I. (Rate/Rank)'),
      unit: grades.map(() => 'Unit Name'),
    },
  };
}

function kinds(
  id: string,
  title: string,
  columns: readonly CostDisplayColumn[],
  values: readonly string[]
): CostDisplaySection {
  return {
    id,
    title,
    rowCount: values.length,
    columns,
    placeholders: { kind: values },
  };
}

function rows(
  id: string,
  title: string,
  rowCount: number,
  columns: readonly CostDisplayColumn[]
): CostDisplaySection {
  return { id, title, rowCount, columns };
}

const SHARED_TAIL = (counts: {
  purchases: number;
  travel: number;
  reserve: number;
  aux: number;
  prfa: number;
  mipr: number;
  contractors: number;
}): CostDisplaySection[] => [
  {
    id: 'msl',
    title: 'Marine safety lab',
    rowCount: 1,
    columns: MSL,
    placeholders: { name: ['Lab Analysis'] },
  },
  rows('purchases', 'Purchases', counts.purchases, amount('Purchase')),
  rows('travel', 'Travel orders', counts.travel, TRAVEL),
  rows('reserve', 'Reserve salary', counts.reserve, amount('Name')),
  rows('aux', 'Auxiliary patrols', counts.aux, amount('Name')),
  rows('prfa', 'PRFA', counts.prfa, amount('Agency')),
  rows('mipr', 'MIPR', counts.mipr, amount('Agency')),
  rows('contractors', 'Contractors', counts.contractors, amount('Contractor')),
];

export const CG_COST_SECTIONS: Record<
  CgCostFormId,
  readonly CostDisplaySection[]
> = {
  CG_5136: [
    personnel(PAY_GRADES_FY25),
    kinds('vehicles', 'Vehicles', VEHICLE, [
      '(1100) Sedan-Midsize',
      '(1200) Sedan-Compact',
      '(1300) Sedan-Subcompact',
    ]),
    kinds('aircraft', 'Aircraft', AIRCRAFT, [
      'MH-60T Jayhawk',
      'HC-130H Hercules',
    ]),
    kinds('boats', 'Boats', BOAT, [
      "29' Response Boat, Small",
      "45' Response Boat, Medium (RBM)",
    ]),
    kinds('cutters', 'Cutters', BOAT, [
      'WMSL-418 National Security Cutter',
      'WPC-154 Fast Response Cutter',
    ]),
    kinds('equipment', 'Equipment', EQUIPMENT, [
      'All Terrain Vehicle (ATV)',
      'AreaRAE',
      'FIRSTLOOK 110',
    ]),
    ...SHARED_TAIL({
      purchases: 4,
      travel: 4,
      reserve: 4,
      aux: 4,
      prfa: 4,
      mipr: 4,
      contractors: 4,
    }),
  ],
  CG_5136X: [
    personnel(['E-1', 'E-2', 'E-3', 'E-4']),
    kinds('vehicles', 'Vehicles', VEHICLE, ['N/A', 'N/A']),
    kinds('aircraft', 'Aircraft', AIRCRAFT, ['N/A', 'N/A']),
    kinds('boats', 'Boats', BOAT, ['N/A', 'N/A']),
    kinds('cutters', 'Cutters', BOAT, ['N/A', 'N/A']),
    kinds('equipment', 'Equipment', EQUIPMENT, ['N/A', 'N/A']),
    ...SHARED_TAIL({
      purchases: 4,
      travel: 3,
      reserve: 3,
      aux: 3,
      prfa: 3,
      mipr: 3,
      contractors: 3,
    }),
  ],
};

export const CG_COST_TITLES: Record<CgCostFormId, string> = {
  CG_5136: 'Federal Project Cost Summary CG-5136',
  CG_5136X: 'Federal Project Cost Summary CG-5136X',
};
